<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="Super Admin" title="Overview" />
      </div>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="text-muted mt-3 mb-0">Loading statistics...</p>
      </div>
      <div v-else-if="loadError" class="text-center py-5">
        <p class="text-danger mb-0">{{ loadError }}</p>
        <button class="btn btn-outline-primary btn-sm mt-3" @click="load">Retry</button>
      </div>

      <template v-else>
        <!-- Indicateurs clés -->
        <div class="row">
          <div class="col-xl-3 col-md-6" v-for="kpi in kpis" :key="kpi.label">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1">{{ kpi.label }}</p>
                    <h4 class="mb-0">{{ kpi.value }}</h4>
                    <small class="text-muted" v-if="kpi.hint">{{ kpi.hint }}</small>
                  </div>
                  <div class="kpi-icon">
                    <feather :type="kpi.icon" size="26"></feather>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Répartition des abonnements -->
          <div class="col-lg-4">
            <div class="card">
              <div class="card-header"><h5>Subscriptions by status</h5></div>
              <div class="card-body">
                <ul class="list-unstyled mb-0">
                  <li
                    class="d-flex justify-content-between align-items-center py-1"
                    v-for="row in statusRows"
                    :key="row.status"
                  >
                    <span :class="statusClass(row.status)">{{ row.status }}</span>
                    <strong>{{ row.count }}</strong>
                  </li>
                  <li v-if="!statusRows.length" class="text-muted">No subscriptions yet.</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Dernières inscriptions -->
          <div class="col-lg-4">
            <div class="card">
              <div class="card-header"><h5>Recent signups</h5></div>
              <div class="card-body p-0">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr><th>Email</th><th>Stores</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="u in recentUsers" :key="u.id">
                      <td class="text-truncate" style="max-width:180px">{{ u.email }}</td>
                      <td>{{ u.storeCount }}</td>
                      <td>
                        <span :class="u.status ? 'text-success' : 'text-danger'">
                          {{ u.status ? 'Active' : 'Disabled' }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!recentUsers.length">
                      <td colspan="3" class="text-muted">No users yet.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Activité récente -->
          <div class="col-lg-4">
            <div class="card">
              <div class="card-header"><h5>Recent activity</h5></div>
              <div class="card-body p-0">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr><th>Date</th><th>User</th><th>Event</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="ev in recentEvents" :key="ev.id">
                      <td>{{ formatDate(ev.createdAt) }}</td>
                      <td class="text-truncate" style="max-width:140px">{{ ev.userEmail || '—' }}</td>
                      <td>{{ ev.eventType }}</td>
                    </tr>
                    <tr v-if="!recentEvents.length">
                      <td colspan="3" class="text-muted">No events yet.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";

export default {
  name: "AdminOverview",
  data() {
    return {
      stats: {},
      loading: true,
      loadError: ""
    };
  },
  computed: {
    kpis() {
      const s = this.stats || {};
      const users = s.users || {};
      const gmv = s.gmv || {};
      return [
        { label: "Total users", value: this.num(users.total), icon: "users" },
        { label: "Active users", value: this.num(users.active), icon: "user-check" },
        { label: "New (30 days)", value: this.num(users.new30d), icon: "user-plus" },
        { label: "Active subscriptions", value: this.num(s.activeSubscriptions), icon: "credit-card" },
        { label: "MRR", value: this.money(s.mrr), icon: "dollar-sign", hint: "Sum of active plans" },
        { label: "Stores", value: this.num(s.stores), icon: "box" },
        {
          label: "Storefront GMV",
          value: this.money(gmv.amount),
          icon: "shopping-cart",
          hint: this.num(gmv.orders) + " paid orders"
        }
      ];
    },
    statusRows() {
      const byStatus = (this.stats || {}).subscriptionsByStatus || {};
      return Object.keys(byStatus).map((status) => ({
        status: status,
        count: byStatus[status]
      }));
    },
    recentUsers() {
      return (this.stats || {}).recentUsers || [];
    },
    recentEvents() {
      return (this.stats || {}).recentEvents || [];
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const res = await firebase.api.get("/admin/stats");
        this.stats = res || {};
      } catch (e) {
        this.loadError = "Could not load statistics: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    num(value) {
      return Number(value || 0).toLocaleString();
    },
    money(value) {
      return "$" + Number(value || 0).toFixed(2);
    },
    statusClass(status) {
      if (status === "active" || status === "trialing") return "text-success text-capitalize";
      if (status === "past_due") return "text-warning text-capitalize";
      if (status === "canceled") return "text-danger text-capitalize";
      return "text-muted text-capitalize";
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(64, 80, 246, 0.1);
  color: #4050f6;
  flex: 0 0 auto;
}
</style>
